import type { ArchitectInvite, CRMUser, EnhancedDeal, TimberProject } from '../types';
import { isArchitectRole } from './userRoles';

export function normalizeUidList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of raw) {
    const uid = typeof item === 'string' ? item.trim() : '';
    if (!uid || seen.has(uid)) continue;
    seen.add(uid);
    out.push(uid);
  }
  return out;
}

export function listArchitects(crmUsers: CRMUser[]): CRMUser[] {
  return crmUsers.filter((u) => u.isActive && isArchitectRole(u.role));
}

export type AssignableArchitect = {
  uid: string;
  displayName: string;
  pending?: boolean;
};

/** Accepted architects plus pending invites that already have an Auth uid. */
export function listAssignableArchitects(
  crmUsers: CRMUser[],
  invites: Array<Pick<ArchitectInvite, 'status' | 'inviteKind' | 'authUid' | 'name' | 'email'>>,
): AssignableArchitect[] {
  const active = listArchitects(crmUsers)
    .map((u) => ({
      uid: u.uid,
      displayName: (u.displayName || u.email || u.uid).trim(),
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
  const seen = new Set(active.map((a) => a.uid));
  const pending: AssignableArchitect[] = [];
  for (const inv of invites) {
    if (inv.status !== 'pending' || inv.inviteKind !== 'architect') continue;
    const uid = (inv.authUid || '').trim();
    if (!uid || seen.has(uid)) continue;
    seen.add(uid);
    const label = (inv.name || inv.email || '').trim() || uid;
    pending.push({ uid, displayName: `${label} (pending)`, pending: true });
  }
  pending.sort((a, b) => a.displayName.localeCompare(b.displayName));
  return [...active, ...pending];
}

/** Open projects that share a deal are granted together. */
export function architectDealProjectIds(
  projectId: string,
  projects: Array<{ id?: string; dealId?: string; status?: string }>,
): string[] {
  const project = projects.find((p) => p.id === projectId);
  if (!project?.id) return [];
  if (!project.dealId) return [project.id];
  const ids = projects
    .filter((p) => p.dealId === project.dealId && p.id && p.status !== 'completed')
    .map((p) => p.id as string);
  return ids.length ? ids : [project.id];
}

export type ArchitectPortalGrantPlan = {
  deals: Array<{ id: string; assignedArchitectUids: string[] }>;
  projects: Array<{ id: string; architectUids: string[] }>;
};

/** Deal lists are the source of truth; each selected project is updated to match. */
export function architectPortalGrantPlan(input: {
  uid: string;
  projectIds: string[];
  projects: Array<{ id?: string; dealId?: string; architectUids?: string[] }>;
  deals: Array<{ id?: string; assignedArchitectUids?: string[] }>;
}): ArchitectPortalGrantPlan {
  const uid = input.uid.trim();
  const deals: ArchitectPortalGrantPlan['deals'] = [];
  const projects: ArchitectPortalGrantPlan['projects'] = [];
  if (!uid) return { deals, projects };

  const dealUids = new Map<string, string[]>();
  const seenProjects = new Set<string>();

  for (const projectId of input.projectIds) {
    const project = input.projects.find((p) => p.id === projectId);
    if (!project?.id || seenProjects.has(project.id)) continue;
    seenProjects.add(project.id);

    let uids: string[];
    if (project.dealId) {
      const cached = dealUids.get(project.dealId);
      if (cached) {
        uids = cached;
      } else {
        const deal = input.deals.find((d) => d.id === project.dealId);
        uids = normalizeUidList([...(deal?.assignedArchitectUids || []), uid]);
        dealUids.set(project.dealId, uids);
        deals.push({ id: project.dealId, assignedArchitectUids: uids });
      }
    } else {
      uids = normalizeUidList([...(project.architectUids || []), uid]);
    }
    projects.push({ id: project.id, architectUids: uids });
  }

  return { deals, projects };
}

export function getAssignedArchitectUids(
  deal: EnhancedDeal | null | undefined,
  project?: TimberProject | null,
): string[] {
  const fromDeal = normalizeUidList(deal?.assignedArchitectUids);
  if (fromDeal.length) return fromDeal;
  return normalizeUidList(project?.architectUids);
}

export function projectHasArchitectUid(
  project: TimberProject | null | undefined,
  deal: EnhancedDeal | null | undefined,
  uid: string | undefined,
): boolean {
  if (!uid) return false;
  return getAssignedArchitectUids(deal, project).includes(uid);
}

/** Fields architects must not keep in client memory from timberProjects snapshots. */
export function stripArchitectHiddenProjectFields<T extends Record<string, unknown>>(data: T): Omit<
  T,
  'value' | 'standupNote' | 'standupNoteAudioUrl' | 'standupNoteUpdatedAt' | 'standupNoteUpdatedBy' | 'standupNoteUpdatedByName'
> {
  const {
    value: _omitValue,
    standupNote: _sn,
    standupNoteAudioUrl: _sa,
    standupNoteUpdatedAt: _st,
    standupNoteUpdatedBy: _sb,
    standupNoteUpdatedByName: _snn,
    ...safe
  } = data;
  return safe;
}

export function architectDisplayName(
  uid: string,
  crmUsers: CRMUser[],
): string {
  return crmUsers.find((u) => u.uid === uid)?.displayName || uid;
}
